import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Plus, Edit2, Trash2, X, Check, FileText } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Input } from "../../../components/ui/input";
import { cn } from "../../../lib/utils";
import { toast } from "../../../lib/toast";
import { LIMITS } from "../../../lib/constants";
import { useCharLimitHandlers } from "../../../components/CharLimitIndicator";
import type { Topico } from "../../../services/topico.service";

interface TopicSidebarProps {
  topicos: Topico[];
  selectedTopicId: string | null;
  activeSectionId?: string;
  onSelectTopic: (topicId: string | null) => void;
  onAddTopic: (title: string) => Promise<void>;
  onUpdateTopicTitle: (topicoId: string, newTitle: string) => Promise<void>;
  onDeleteTopic: (topicoId: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  canEdit?: boolean;
}

export function TopicSidebar({
  topicos,
  selectedTopicId,
  activeSectionId,
  onSelectTopic,
  onAddTopic,
  onUpdateTopicTitle,
  onDeleteTopic,
  collapsed = false,
  onToggleCollapse,
  canEdit = true,
}: TopicSidebarProps) {
  const { t } = useTranslation('common');
  const [hoveredTopicId, setHoveredTopicId] = useState<string | null>(null);
  const [newTopicTitle, setNewTopicTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const newTopicInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const newTopicRef = useRef<HTMLDivElement>(null);

  const newTopicHandlers = useCharLimitHandlers(LIMITS.TOPIC_TITLE_MAX, setNewTopicTitle);
  const editTopicHandlers = useCharLimitHandlers(LIMITS.TOPIC_TITLE_MAX, setEditingTitle);

  // Criar tópico inline
  const handleCreateTopic = useCallback(async () => {
    const trimmedTitle = newTopicTitle.trim();
    if (!trimmedTitle) return;

    if (trimmedTitle.length > LIMITS.TOPIC_TITLE_MAX) {
      toast.error(`Título do tópico excedeu o limite de ${LIMITS.TOPIC_TITLE_MAX} caracteres. Reduza o texto.`);
      return;
    }

    setIsCreating(true);
    try {
      await onAddTopic(trimmedTitle);
      setNewTopicTitle("");
      setTimeout(() => {
        newTopicRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      toast.success(t('topicSidebar.topic_created'));
    } catch (error: any) {
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network')) {
        toast.error('Não foi possível criar o tópico — verifique sua conexão.');
      } else {
        toast.error(msg || 'Não foi possível criar o tópico. Tente novamente.');
      }
    } finally {
      setIsCreating(false);
    }
  }, [newTopicTitle, onAddTopic, t]);

  const handleNewTopicKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateTopic();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setNewTopicTitle("");
      newTopicInputRef.current?.blur();
    }
  }, [handleCreateTopic]);

  // Edição inline do título
  const startEditingTopic = useCallback((topico: Topico) => {
    setEditingTopicId(topico.id);
    setEditingTitle(topico.titulo);
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 50);
  }, []);

  const cancelEditingTopic = useCallback(() => {
    setEditingTopicId(null);
    setEditingTitle("");
  }, []);

  const saveEditingTopic = useCallback(async () => {
    if (!editingTopicId || !editingTitle.trim()) {
      cancelEditingTopic();
      return;
    }

    if (editingTitle.trim().length > LIMITS.TOPIC_TITLE_MAX) {
      toast.error(`Título do tópico excedeu o limite de ${LIMITS.TOPIC_TITLE_MAX} caracteres. Reduza o texto.`);
      return;
    }

    const originalTopic = topicos.find(t => t.id === editingTopicId);
    if (originalTopic && originalTopic.titulo === editingTitle.trim()) {
      cancelEditingTopic();
      return;
    }

    setIsSavingEdit(true);
    try {
      await onUpdateTopicTitle(editingTopicId, editingTitle.trim());
      toast.success(t('topicSidebar.topic_updated'));
    } catch (error: any) {
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network')) {
        toast.error('Não foi possível atualizar o tópico — verifique sua conexão.');
      } else {
        toast.error(msg || 'Não foi possível atualizar o tópico. Tente novamente.');
      }
    } finally {
      setIsSavingEdit(false);
      setEditingTopicId(null);
      setEditingTitle("");
    }
  }, [editingTopicId, editingTitle, topicos, onUpdateTopicTitle, cancelEditingTopic, t]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter") {
      e.preventDefault();
      saveEditingTopic();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditingTopic();
    }
  }, [saveEditingTopic, cancelEditingTopic]);

  // Estado colapsado - botão integrado no layout
  if (collapsed) {
    return (
      <div className="h-full bg-background border-r border-border flex flex-col">
        <div className="p-2 border-b border-border">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleCollapse}
            aria-label={t('topicSidebar.expand_sidebar')}
            className="w-full"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center pt-2 gap-1">
          <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center" title={t('topicSidebar.topics')}>
            <FileText size={16} className="text-muted-foreground" />
          </div>
          {topicos.length > 0 && (
            <span className="text-xs text-muted-foreground">{topicos.length}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-background">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">{t('topicSidebar.topics')}</h3>
        </div>
        <button
          onClick={onToggleCollapse}
          className="p-1.5 hover:bg-muted rounded-md"
          aria-label={t('topicSidebar.collapse_sidebar')}
        >
          <ChevronLeft size={18} className="text-muted-foreground" />
        </button>
      </div>

      {/* Input inline para adicionar */}
      {canEdit && (
        <div className="p-3 border-b border-border">
          <div className="flex gap-2">
            <Input
              ref={newTopicInputRef}
              value={newTopicTitle}
              onChange={newTopicHandlers.onChange}
              onPaste={newTopicHandlers.onPaste}
              maxLength={LIMITS.TOPIC_TITLE_MAX}
              onKeyDown={handleNewTopicKeyDown}
              placeholder={t('topicSidebar.new_topic_placeholder')}
              className="flex-1 h-9 text-sm"
              disabled={isCreating}
            />
            <Button
              size="sm"
              onClick={handleCreateTopic}
              disabled={!newTopicTitle.trim() || isCreating}
              className="px-3"
            >
              {isCreating ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Plus size={16} />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <ScrollArea className="flex-1">
        {/* Empty state */}
        {topicos.length === 0 && !isCreating && (
          <div className="p-6 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/20 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nenhum tópico ainda</p>
            <p className="text-xs text-muted-foreground mt-1">Digite acima para adicionar</p>
          </div>
        )}

        {/* Topics list */}
        {topicos.length > 0 && (
          <div className="p-3">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Lista
              </span>
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {topicos.length}
              </span>
            </div>

            <div className="space-y-1">
              {topicos.map((topico, index) => {
                // Apenas activeSectionId controla o destaque visual
                const isActive = activeSectionId === topico.id;
                const isLast = index === topicos.length - 1;
                const isEditing = editingTopicId === topico.id;
                const isHovered = hoveredTopicId === topico.id;

                return (
                  <div
                    key={topico.id}
                    ref={isLast ? newTopicRef : null}
                    className={cn(
                      "rounded-lg border",
                      isActive
                        ? "bg-primary/5 border-primary/30"
                        : "bg-background border-transparent hover:border-border hover:bg-muted/30"
                    )}
                    onMouseEnter={() => !isEditing && setHoveredTopicId(topico.id)}
                    onMouseLeave={() => setHoveredTopicId(null)}
                  >
                    {isEditing ? (
                      <div className="p-2 flex items-center gap-2">
                        <Input
                          ref={editInputRef}
                          value={editingTitle}
                          onChange={editTopicHandlers.onChange}
                          onPaste={editTopicHandlers.onPaste}
                          maxLength={LIMITS.TOPIC_TITLE_MAX}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveEditingTopic}
                          disabled={isSavingEdit}
                          className="flex-1 h-8 text-sm"
                          autoComplete="off"
                        />
                        <button
                          onClick={saveEditingTopic}
                          disabled={isSavingEdit}
                          className="p-1.5 hover:bg-green-50 rounded text-green-600"
                          title="Salvar"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={cancelEditingTopic}
                          disabled={isSavingEdit}
                          className="p-1.5 hover:bg-muted rounded text-muted-foreground"
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onSelectTopic(topico.id)}
                        onDoubleClick={(e) => {
                          if (!canEdit) return;
                          e.preventDefault();
                          startEditingTopic(topico);
                        }}
                        className="w-full px-3 py-2.5 text-left flex items-center gap-3"
                      >
                        <div className={cn(
                          "w-1.5 h-1.5 rounded-full flex-shrink-0",
                          isActive ? "bg-primary" : "bg-muted-foreground/30"
                        )} />
                        <span className={cn(
                          "text-sm flex-1 truncate",
                          isActive ? "font-medium text-primary" : "text-foreground"
                        )}>
                          {topico.titulo}
                        </span>
                        {isHovered && canEdit && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingTopic(topico);
                              }}
                              className="p-1 hover:bg-muted rounded"
                              title="Editar"
                            >
                              <Edit2 size={12} className="text-muted-foreground" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteTopic(topico.id);
                              }}
                              className="p-1 hover:bg-destructive/10 rounded"
                              title="Excluir"
                            >
                              <Trash2 size={12} className="text-destructive" />
                            </button>
                          </div>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
